<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'
import { useStore } from '@/store'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const { t } = useI18n()
const store = useStore()
const route = useRoute()

const props = withDefaults(defineProps<{
  administrativeLocalStayId: string | null
  pmsiId: string | null
  pmsiType: PmsiTypeEnum
  stayId: number
  rhsId?: number | null
}>(), {
  rhsId: null,
})

const numberJustCopied = ref(false)
const linkJustCopied = ref(false)

// Copie possible via l'API Clipboard (contexte sécurisé) ou le fallback execCommand (HTTP) ; sinon ni tooltip ni curseur cliquable.
const canCopy = (Boolean(navigator.clipboard) && window.isSecureContext)
  || (typeof document.queryCommandSupported === 'function' && document.queryCommandSupported('copy'))

const hasMaintenancePermission = computed(
  () => hasPermission(RoleEnum.ROLE_MAINTENANCE_VIEWER, store.state.login.userRole),
)
const settingsUrlQuery = computed(() => route.query)

const historyRouteConfig = computed(() => ({
  name: 'maintenance-stay-history',
  params: {
    pmsiType: props.pmsiType,
    pmsiId: props.pmsiId,
  },
  query: {
    ...settingsUrlQuery.value,
    stayId: props.stayId,
    ...(props.rhsId ? { rhsId: props.rhsId } : {}),
  },
}))

const copyNumberTooltip = computed(() => ({
  content: numberJustCopied.value
    ? t('stayDisplayer.generalStayData.numberCopied')
    : t('stayDisplayer.generalStayData.copyNumber'),
  hideTriggers: (events: string[]) => events.filter((e) => e !== 'click'),
}))

const copyLinkTooltip = computed(() => {
  return {
    content: linkJustCopied.value
      ? t(props.rhsId ? 'stayDisplayer.generalStayData.rhsLinkCopied' : 'stayDisplayer.generalStayData.stayLinkCopied')
      : t(props.rhsId ? 'stayDisplayer.generalStayData.copyRhsLink' : 'stayDisplayer.generalStayData.copyStayLink'),
    hideTriggers: (events: string[]) => events.filter((e) => e !== 'click'),
  }
})

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
    // fallback execCommand for HTTP envs (qa-preprod or auto generated cert issues)
    const tmptxtArea = document.createElement('textarea')
    tmptxtArea.value = text
    tmptxtArea.style.position = 'fixed'
    tmptxtArea.style.opacity = '0'
    document.body.appendChild(tmptxtArea)
    tmptxtArea.select()
    const succeeded = document.execCommand('copy')
    document.body.removeChild(tmptxtArea)
    return succeeded
  } catch {
    return false
  }
}

async function copyStayNumber() {
  if (!props.administrativeLocalStayId) return
  numberJustCopied.value = await copyToClipboard(props.administrativeLocalStayId)
}

async function copyStayUrl() {
  linkJustCopied.value = await copyToClipboard(window.location.href)
}
</script>

<template>
  <div class="d-flex align-items-center gap-2">
    <div
      v-tooltip="canCopy && administrativeLocalStayId ? copyNumberTooltip : undefined"
      class="d-flex align-items-center fw-bold administrative-number"
      :class="{ cp: canCopy && administrativeLocalStayId }"
      @click="copyStayNumber"
      @mouseenter="numberJustCopied = false"
    >
      <span>#</span>
      <span>{{ administrativeLocalStayId || '-' }}</span>
    </div>
    <span
      v-tooltip="canCopy ? copyLinkTooltip : undefined"
      class="d-flex align-items-center copy-link-icon"
      :class="{ cp: canCopy }"
      @click="copyStayUrl"
      @mouseenter="linkJustCopied = false"
    >
      <sancare-octicon
        name="link"
        :width="18"
        :height="18"
      />
    </span>
    <div
      v-if="$slots.default"
      class="ms-auto"
    >
      <slot />
    </div>
    <router-link
      v-if="hasMaintenancePermission && pmsiId"
      class="btn btn-link p-0"
      :class="{ 'ms-auto': !$slots.default }"
      :to="historyRouteConfig"
    >
      {{ t('stayDisplayer.generalStayData.history') }}
    </router-link>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';

// .administrative-number itself lives in assets/styles/general-stay-data.scss (global) — shared
// with StayQuickView.vue's aside (BUILD-802), which shows the same stay number outside this header.

.copy-link-icon {
  color: $sancare-dark-grey;

  :deep(svg.octicon) {
    fill: currentcolor;
    vertical-align: middle;
  }
}
</style>
